import { HttpMethod, getLocale, type Nullable, Locale } from '@libs/core';
import type {
    CustomerPatchBodyRespnse,
    CustomerPatchBody,
} from '@libs/common-next';
import { getNextApiRoute, typedFetch } from '@libs/common-next';
import type {
    InventoryResult,
    Store,
    StoreSearchResult,
} from '@libs/common-node';
import { checkStoreInventory } from '@libs/algolia';
import { hasStock, validateStockStatus } from '../../utils/stockStatusUtils';
import type { GeolocationResponse, UserLocation } from './types';

export const updateStore = async (
    data: CustomerPatchBody
): Promise<Nullable<CustomerPatchBodyRespnse>> =>
    await typedFetch<CustomerPatchBodyRespnse>(getNextApiRoute(`store`), {
        headers: {
            accept: 'application/json',
            'content-type': 'application/json',
        },
        body: JSON.stringify(data),
        method: HttpMethod.PATCH,
    });

type AlertTextType = {
    title: string;
    text: string | React.ReactNode;
    link?: string;
    linkText?: string;
};

const GENERAL_ALERT: AlertTextType = {
    title: 'No locations found',
    text: 'Please try again',
};

export const getAlertText = (searchText: string): AlertTextType => {
    const isCurrentLocaleUS = getLocale() !== Locale['en-CA'];

    if (searchText === '') {
        return GENERAL_ALERT;
    }

    // check if usser is trying to search an US postal code on the .ca site
    if (!isCurrentLocaleUS) {
        const isUSPostalCode = /^\d{5}(?:[-\s]\d{4})?$/.test(searchText);

        if (isUSPostalCode) {
            return {
                title: 'Searching for a store in the United States?',
                text: 'Please go to ',
                link: 'https://www.petsmart.com',
                linkText: 'petsmart.com',
            };
        }
    }

    // check if user is trying to search Canadian postal codes on the .com site
    if (isCurrentLocaleUS) {
        const isCAPostalCode =
            /^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVXY][ -]?\d[ABCEGHJKLMNPRSTVXY]\d$/i.test(
                searchText
            );

        if (isCAPostalCode) {
            return {
                title: 'Searching for a store in Canada?',
                text: 'Please go to ',
                link: 'https://www.petsmart.ca',
                linkText: 'petsmart.ca',
            };
        }
    }

    return GENERAL_ALERT;
};

export const updateStoreGuestUser = async (
    data: CustomerPatchBody
): Promise<Nullable<CustomerPatchBodyRespnse>> =>
    await typedFetch<CustomerPatchBodyRespnse>(getNextApiRoute(`store`), {
        headers: {
            accept: 'application/json',
            'content-type': 'application/json',
        },
        body: JSON.stringify(data),
        method: HttpMethod.POST,
    });

const storeInventoryPart = ({
    inventoryStore,
    inventory,
    currentVariantId,
    minimumCaseQty = 1,
}: {
    inventoryStore: StoreSearchResult;
    inventory: InventoryResult[];
    currentVariantId: number;
    minimumCaseQty?: number;
}): Partial<Store> => {
    const storeInventory = checkStoreInventory({
        inventoryStore,
        inventory,
        currentVariantId,
    });

    const currentStoreStockstatus = validateStockStatus({
        stockQuantity: storeInventory as number,
        minimumCaseQty,
    });

    return {
        CurrentProductStockstatus: currentStoreStockstatus,
        HasCurrentProductInStock: hasStock(currentStoreStockstatus)
            ? storeInventory
            : 0,
    };
};

export const addStoreSkuInventoryToResult = (
    storeInventory: InventoryResult[],
    storeList: StoreSearchResult[],
    currentVariantId: number,
    minimumCaseQty: number
) =>
    storeList.map((s: StoreSearchResult) => ({
        ...s,
        Store: {
            ...s.Store,
            ...storeInventoryPart({
                inventoryStore: s,
                inventory: storeInventory,
                currentVariantId,
                minimumCaseQty,
            }),
        },
    }));

export const storeSearchHasStock = (s: StoreSearchResult) =>
    s.Store?.CurrentProductStockstatus &&
    hasStock(s.Store.CurrentProductStockstatus);

export const convertStoreIdToDemandwareFormat = (id: number) =>
    id.toString().padStart(4, '0');

function getCurrentPositionPromise(): Geolocation | unknown {
    return new Promise((resolve, reject) => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(resolve, reject);
        } else {
            reject(new Error('Geolocation is not supported by this browser.'));
        }
    });
}

/*
    Safari will keep asking the user if they want to share their location if they refresh the page.
    But it does not show on page load.
*/
export const getUserLocation = async (): Promise<UserLocation> => {
    let location: UserLocation = { isGeolocationEnabled: false };
    if (navigator.permissions) {
        try {
            const result = await navigator.permissions.query({
                name: 'geolocation',
            });
            if (result.state === 'prompt' || result.state === 'granted') {
                const promise: GeolocationResponse =
                    (await getCurrentPositionPromise()) as GeolocationResponse;
                location = {
                    latitude: promise?.coords?.latitude,
                    longitude: promise?.coords?.longitude,
                    isGeolocationEnabled: true,
                };
            }
        } catch (e) {
            console.log('User denied location services');
        }
    }
    console.error('Permissions API is not supported in this browser.');
    return location;
};
